// Pääskriptin orkestrointi: kytkee blokkauksen, GCM:n, tallennuksen,
// API-kutsut ja käyttöliittymän yhteen. Vastaa vaihe 0:n cmp-script-edge-
// funktion palauttamaa IIFE:ä käyttäytymiseltään identtisesti.

import type { BlockedEntry, BlockPatterns, Choices, CmpConfig, GcmMapping } from './types';
import { getValidConsent, getConsentId, migrateProofId, storeConsent as storageStoreConsent, consentKey, gcmKey, patternsKey } from './storage';
import { FALLBACK_PATTERNS, shouldBlockSrc } from './patterns';
import { configFingerprint, consentCoversConfig } from './config-version';
import { cleanupDeniedStorage } from './cleanup';
import {
  blockIframe,
  IFRAME_FALLBACK_PATTERNS,
  releaseIframe,
  shouldReleaseIframe,
  type BlockedIframe,
} from './iframes';
import { fetchBannerConfig, sendConsent as apiSendConsent } from './api-client';
import { renderBanner, showConfirmation, type BannerUiContext } from './ui';

export interface CmpInitOptions {
  domain: string;
  apiBase: string;
}

/**
 * Käynnistää CMP:n: aloittaa blokkauksen heti (jos inline-blokkeri ei ole jo
 * tehnyt sitä), hakee konfiguraation ja joko soveltaa tallennetun
 * suostumuksen tai näyttää bannerin.
 */
export function initCmp(options: CmpInitOptions): void {
  const domain = options.domain;
  const API_BASE = options.apiBase;
  const STORAGE_KEY = consentKey(domain);
  let CONFIG: CmpConfig | null = null;
  const LANG = document.documentElement.lang || navigator.language?.substring(0, 2) || 'fi';

  // Todistustunnuksen avaimen migraatio vanhasta nimestä (V2-T1, PL-5).
  // Tehdään heti latauksessa, jotta vanha avain ei jää kävijän laitteelle
  // roikkumaan seuraavaan valintaan asti. Uudelle kävijälle no-op.
  migrateProofId();

  // ===== SCRIPT BLOCKING =====
  // Inline-blokkeri on ensisijainen; pääskriptin oma blokkaus on fallback
  // sivuille, joilta inline-blokkeri puuttuu.
  const _hasInlineBlocker = !!window.__cmpBlocking;

  const _blockedScripts: BlockedEntry[] = _hasInlineBlocker ? window.__cmpBlocking!.blocked : [];
  // Upotusten esto (V2-T7): inline-blokkeri hoitaa tämän, jos se on asennettu.
  // Fallback-polulla pääskripti tekee saman itse.
  const _blockedIframes: BlockedIframe[] = [];
  const _iframePatterns: BlockPatterns = {};
  const _blockPatterns: BlockPatterns = _hasInlineBlocker ? window.__cmpBlocking!.patterns : {};
  let _observer: MutationObserver | null = _hasInlineBlocker ? window.__cmpBlocking!.observer : null;
  let _consentApplied = false;
  let _currentConsent: Choices | null = _hasInlineBlocker ? window.__cmpBlocking!.consent : null;
  const _origCreate = _hasInlineBlocker ? window.__cmpBlocking!.origCreate : document.createElement.bind(document);

  function initBlockPatterns() {
    if (_hasInlineBlocker) return; // Inline-blokkeri alusti jo patternit
    for (const slug in FALLBACK_PATTERNS) {
      _blockPatterns[slug] = FALLBACK_PATTERNS[slug].slice();
    }
    for (const slug in IFRAME_FALLBACK_PATTERNS) {
      _iframePatterns[slug] = IFRAME_FALLBACK_PATTERNS[slug].slice();
    }
  }

  /** Estää upotuksen fallback-polulla (kun inline-blokkeria ei ole). */
  function maybeBlockIframe(el: HTMLIFrameElement, consent: Choices | null): void {
    const src = el.getAttribute('src') || '';
    if (!src || src.indexOf('about:') === 0 || src.indexOf('javascript:') === 0) return;
    const match = shouldBlockSrc(_iframePatterns, src, consent);
    if (!match) return;
    _blockedIframes.push(blockIframe(el, match.slug, src, LANG));
    console.log('CMP: blocked iframe [' + match.slug + ']', src);
  }

  function refineBlockPatterns(config: CmpConfig) {
    const cats = config.categories || [];
    cats.forEach(function (cat) {
      if (cat.is_required) return;
      if (!_blockPatterns[cat.slug]) _blockPatterns[cat.slug] = [];
      if (!_iframePatterns[cat.slug]) _iframePatterns[cat.slug] = [];
      (cat.cookies || []).forEach(function (ck) {
        if (ck.provider && _blockPatterns[cat.slug].indexOf(ck.provider) === -1) {
          _blockPatterns[cat.slug].push(ck.provider);
        }
        if (ck.provider && _iframePatterns[cat.slug].indexOf(ck.provider) === -1) {
          _iframePatterns[cat.slug].push(ck.provider);
        }
      });
    });
  }

  function shouldBlock(src: string | null | undefined, consent: Choices | null | undefined) {
    if (_hasInlineBlocker) return window.__cmpBlocking!.shouldBlock(src, consent);
    return shouldBlockSrc(_blockPatterns, src, consent);
  }

  if (!_hasInlineBlocker) {
    // === createElement-override (ensisijainen fallback-esto) ===
    document.createElement = function (tag: string) {
      const el = _origCreate(tag);
      if (tag.toLowerCase() === 'script') {
        let _realSrc = '';
        Object.defineProperty(el, 'src', {
          get: function () { return _realSrc; },
          set: function (val: string) {
            const match = shouldBlock(val, _currentConsent);
            if (match) {
              (el as HTMLScriptElement).type = 'text/plain';
              el.setAttribute('data-cmp-blocked', match.slug);
              el.setAttribute('data-cmp-original-src', val);
              _blockedScripts.push({ node: el, slug: match.slug, src: val });
              _realSrc = '';
              console.log('CMP: createElement blocked [' + match.slug + ']', val);
              return;
            }
            _realSrc = val;
            el.setAttribute('src', val);
          },
          configurable: true,
        });
      }
      return el;
    } as typeof document.createElement;
  }

  function startBlocking(consent: Choices | null) {
    if (_hasInlineBlocker) return; // Inline-blokkeri hoitaa tämän
    if (_observer) return;
    _observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        m.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          const tag = (node as Element).tagName;
          if (tag === 'SCRIPT') {
            const el = node as HTMLScriptElement;
            const src = el.src || el.getAttribute('src');
            const match = shouldBlock(src, consent);
            if (match) {
              el.type = 'text/plain';
              el.setAttribute('data-cmp-blocked', match.slug);
              el.setAttribute('data-cmp-original-src', src as string);
              _blockedScripts.push({ node: el, slug: match.slug, src: src as string });
              console.log('CMP: blocked script [' + match.slug + ']', src);
            }
          } else if (tag === 'IFRAME') {
            maybeBlockIframe(node as HTMLIFrameElement, consent);
          } else if ((node as Element).querySelectorAll) {
            const iframes = (node as Element).querySelectorAll('iframe');
            for (let i = 0; i < iframes.length; i++) {
              maybeBlockIframe(iframes[i] as HTMLIFrameElement, consent);
            }
          }
        });
      });
    });
    _observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  // Staattisesti portitetut tagit: <script type="text/plain" data-cmp-category="...">
  // aktivoidaan, kun kävijä hyväksyy kyseisen kategorian.
  function activateStaticScripts(choices: Choices) {
    const staticBlocked = document.querySelectorAll('script[type="text/plain"][data-cmp-category]');
    staticBlocked.forEach(function (el) {
      const cat = el.getAttribute('data-cmp-category') as string;
      if (choices[cat]) {
        const ns = _origCreate('script');
        const src = el.getAttribute('data-cmp-src') || el.getAttribute('src');
        if (src) ns.src = src;
        else ns.textContent = el.textContent;
        const attrs = el.attributes;
        for (let j = 0; j < attrs.length; j++) {
          const nm = attrs[j].name;
          if (nm === 'type' || nm === 'src' || nm.indexOf('data-cmp-') === 0) continue;
          ns.setAttribute(nm, attrs[j].value);
        }
        if (el.parentNode) el.parentNode.insertBefore(ns, el.nextSibling);
        el.remove();
        console.log('CMP: activated static script [' + cat + ']', src);
      }
    });
  }

  /** Kategoriakohtainen GCM-mappaus konfiguraatiosta (V2-T6). */
  function gcmMappingFromConfig(config: CmpConfig | null): GcmMapping | null {
    if (!config || !config.categories) return null;
    const mapping: GcmMapping = {};
    config.categories.forEach(function (cat) {
      // null/undefined = käytä oletusmappausta; tyhjä lista = ei ohjaa mitään
      if (cat.gcm_consent_types) mapping[cat.slug] = cat.gcm_consent_types;
    });
    return Object.keys(mapping).length > 0 ? mapping : null;
  }

  function applyConsent(choices: Choices) {
    _consentApplied = true;
    _currentConsent = choices;

    // Hylättyjen kategorioiden evästeet ja tallennusavaimet pois (V2-T8).
    // Ajetaan jokaisella soveltamisella, joten myöhemmin ilmestynyt eväste
    // poistuu seuraavalla sivulatauksella.
    try {
      const siivottu = cleanupDeniedStorage(CONFIG, choices);
      if (siivottu.cookies.length > 0 || siivottu.localStorageKeys.length > 0) {
        console.log('CMP: siivottiin hylätyt tallennukset', siivottu);
      }
    } catch (e) { console.warn('CMP: cleanup failed', e); }

    // Google Consent Mode v2: aseta konfiguroitu mappaus ja lähetä update
    try {
      if (window.__cmpGcm && window.__cmpGcm.enabled) {
        if (typeof window.__cmpGcm.setMapping === 'function') {
          window.__cmpGcm.setMapping(gcmMappingFromConfig(CONFIG));
        }
        if (typeof window.__cmpGcm.update === 'function') {
          window.__cmpGcm.update(choices);
        }
      }
    } catch (e) { console.warn('CMP: gcm update failed', e); }

    // Staattiset skriptit aktivoidaan aina, oli inline-blokkeri tai ei
    activateStaticScripts(choices);

    if (_hasInlineBlocker) {
      // Dynaamisten skriptien ja upotusten vapautus delegoidaan blokkerille
      window.__cmpBlocking!.apply(choices);
      return;
    }

    // Vapauta hyväksyttyjen kategorioiden upotukset (fallback-polku)
    for (let i = _blockedIframes.length - 1; i >= 0; i--) {
      if (shouldReleaseIframe(_blockedIframes[i].slug, choices)) {
        releaseIframe(_blockedIframes[i]);
        _blockedIframes.splice(i, 1);
      }
    }

    // Vapauta hyväksyttyjen kategorioiden skriptit
    const toUnblock: BlockedEntry[] = [];
    for (let i = _blockedScripts.length - 1; i >= 0; i--) {
      const entry = _blockedScripts[i];
      if (choices[entry.slug]) {
        toUnblock.push(entry);
        _blockedScripts.splice(i, 1);
      }
    }
    toUnblock.forEach(function (entry) {
      const newScript = _origCreate('script');
      newScript.setAttribute('src', entry.src);
      const attrs = entry.node.attributes;
      for (let j = 0; j < attrs.length; j++) {
        const name = attrs[j].name;
        if (name === 'type' || name === 'src' || name.indexOf('data-cmp-') === 0) continue;
        newScript.setAttribute(name, attrs[j].value);
      }
      if (entry.node.parentNode) entry.node.parentNode.removeChild(entry.node);
      document.head.appendChild(newScript);
      console.log('CMP: unblocked script [' + entry.slug + ']', entry.src);
    });

    if (_observer) {
      _observer.disconnect();
      _observer = null;
    }
    startBlocking(choices);
  }

  // Aloita blokkaus heti (vain jos inline-blokkeria ei ole)
  if (!_hasInlineBlocker) {
    initBlockPatterns();
    // Ei vapautusta tallennetun suostumuksen perusteella ennen kuin
    // konfiguraatio on ladattu ja suostumus todennettu (V2-T3, PL-14).
    _currentConsent = null;
    startBlocking(null);
  }

  // ===== END SCRIPT BLOCKING =====

  /**
   * Tallennettu suostumus, joka on sekä voimassa (V2-T2) että annettu nykyistä
   * konfiguraatiota vastaan (V2-T3). Muussa tapauksessa null → banneri kysyy
   * uudelleen eikä mitään vapauteta.
   */
  function usableStoredConsent() {
    const stored = getValidConsent(domain);
    if (!stored) return null;
    if (CONFIG && !consentCoversConfig(stored.cv, CONFIG)) return null;
    return stored;
  }

  const uiContext: BannerUiContext = {
    get config() { return CONFIG as CmpConfig; },
    lang: LANG,
    // Vanhentunutta tai konfiguraatiomuutoksen takia rauennutta suostumusta ei
    // näytetä eikä esitäytetä toiselle tasolle: uudelleenkysytty valinta on
    // tehtävä puhtaalta pöydältä (V2-T2, V2-T3).
    getStoredConsent: function () { return usableStoredConsent(); },
    getConsentId: function () { return getConsentId(domain); },
    storeConsent: function (choices, action) {
      storageStoreConsent(
        domain,
        choices,
        action,
        CONFIG && CONFIG.consent_expiry_days,
        CONFIG ? configFingerprint(CONFIG) : null,
      );
    },
    applyConsent: applyConsent,
    sendConsent: function (choices, action, onDone) {
      apiSendConsent(API_BASE, domain, CONFIG as CmpConfig, LANG, choices, action, onDone);
    },
  };

  window.CMP_showBanner = function () {
    if (CONFIG) {
      const el = document.getElementById('cmp-overlay');
      if (el) el.remove();
      renderBanner(uiContext);
    }
  };

  // Init
  fetchBannerConfig(API_BASE, domain)
    .then(function (data) {
      if (data.error) { console.warn('CMP:', data.error); return; }
      CONFIG = data;
      // Säilytä GCM-lippu seuraavan sivulatauksen inline-blokkerille
      try {
        localStorage.setItem(gcmKey(domain), CONFIG.google_consent_mode_enabled === false ? '0' : '1');
      } catch { /* localStorage ei käytettävissä */ }
      // Tarkenna blokkauspatternit konfiguraation datalla
      refineBlockPatterns(CONFIG);
      // Tallenna blocking_patterns inline-blokkerin välimuistiin
      if (CONFIG.blocking_patterns) {
        try { localStorage.setItem(patternsKey(domain), JSON.stringify(CONFIG.blocking_patterns)); } catch { /* localStorage ei käytettävissä */ }
      }
      const stored = usableStoredConsent();
      if (stored) {
        // Voimassa oleva, nykyistä konfiguraatiota vastaava suostumus —
        // vapauta hyväksytyt kategoriat
        applyConsent(stored.choices);
      } else {
        // Ei suostumusta, se on vanhentunut (V2-T2) tai konfiguraation
        // olennainen sisältö on muuttunut (V2-T3) → kysytään uudelleen
        const vanhentunut = getValidConsent(domain);
        if (vanhentunut) {
          console.log('CMP: aiempi suostumus ei kata nykyistä konfiguraatiota — kysytään uudelleen');
        }
        renderBanner(uiContext);
      }
    })
    .catch(function (e) {
      console.warn('CMP: failed to load config', e);
      // Konfiguraatiota ei saatu: olennaista versiota ei voi verrata, joten
      // sovelletaan voimassa olevaa tallennettua suostumusta sellaisenaan
      // (V2-T3, PL-14). Ilman tätä paluukävijän hyväksymät tagit eivät
      // latautuisi lainkaan verkkohäiriön aikana. Ilman suostumusta kaikki
      // pysyy estettynä.
      const stored = getValidConsent(domain);
      if (stored && !_consentApplied) applyConsent(stored.choices);
    });
}

export { showConfirmation };
